import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';
import { InlineError } from '@lean-agility/design-system/react';
import { useUploadLicense } from '../../hooks';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { cn } from '@lean-agility/design-system/react';

interface LicenseUploadFormProps {
  onSuccess?: () => void;
  compact?: boolean;
}

export function LicenseUploadForm({ onSuccess, compact = false }: LicenseUploadFormProps) {
  const [licenseKey, setLicenseKey] = useState('');
  const { mutateAsync, isPending, error, reset } = useUploadLicense();
  const { handleSuccess } = useErrorHandler('License');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = licenseKey.trim();
    if (!trimmed) return;

    try {
      const result = await mutateAsync({ license_key: trimmed });
      handleSuccess(result.message || 'License activated successfully');
      setLicenseKey('');
      onSuccess?.();
    } catch {
      // Error is captured in mutation state
    }
  };

  const errorMessage = error instanceof Error ? error.message : null;

  return (
    <form onSubmit={handleSubmit} className={cn('space-y-4', compact && `space-y-3`)}>
      <div>
        <label htmlFor="license-key" className="mb-1.5 block text-sm font-medium text-text-primary">
          License key
        </label>
        <textarea
          id="license-key"
          data-testid="license-key-input"
          value={licenseKey}
          onChange={(e) => {
            setLicenseKey(e.target.value);
            if (error) reset();
          }}
          placeholder="Paste your base64-encoded license key here..."
          className={cn(
            `w-full rounded-lg border border-border-default bg-bg-base px-3 py-2 font-mono text-sm`,
            `
              text-text-primary
              placeholder:text-text-muted
            `,
            `focus:border-border-accent focus:ring-1 focus:ring-border-accent focus:outline-hidden`,
            'dark:border-border-default/50 dark:bg-bg-elevated',
            compact ? 'h-24' : 'h-32',
          )}
        />
      </div>
      <div data-testid="license-upload-error">
        <InlineError error={errorMessage} dismissible onDismiss={reset} />
      </div>
      <Button
        type="submit"
        disabled={isPending || !licenseKey.trim()}
        size={compact ? 'sm' : 'md'}
        data-testid="activate-license-button"
      >
        {isPending && <Loader2 className="size-4 animate-spin" />}
        Activate License
      </Button>
    </form>
  );
}
